/**
 * 호환 유틸리티 (drop-in) — kingdoo-editor LumirEditor.tsx의
 * `ContentUtils` / `EditorConfig` 클래스 export 패리티.
 *
 * 구 lumir-editor를 쓰던 프로젝트가
 *   import { ContentUtils, EditorConfig } from "@lumir-company/editor";
 * 하던 것을 lumir-editor에서 동일 시그니처로 그대로 쓸 수 있게 한다.
 * 동작은 원본 단위 테스트(ContentUtils.unit.test / EditorConfig.unit.test)와 동일.
 */

/** @typedef {{ type: string, props?: object, content?: any, children?: any[] }} DefaultPartialBlock */

/** 콘텐츠(블록 배열/JSON 문자열) 검증·정규화 유틸 */
export class ContentUtils {
  /** JSON 문자열이 유효한 "배열" JSON 인지 */
  static isValidJSONString(jsonString) {
    try {
      const parsed = JSON.parse(jsonString);
      return Array.isArray(parsed);
    } catch {
      return false;
    }
  }

  /** JSON 문자열 → 블록 배열(배열이 아니거나 실패 시 null) */
  static parseJSONContent(jsonString) {
    try {
      const parsed = JSON.parse(jsonString);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  /** 기본 paragraph 블록 (원본과 동일하게 content 는 빈 text 1개) */
  static createDefaultBlock() {
    return {
      type: 'paragraph',
      props: {
        textColor: 'default',
        backgroundColor: 'default',
        textAlignment: 'left',
      },
      content: [{ type: 'text', text: '', styles: {} }],
      children: [],
    };
  }

  /** 콘텐츠 검증: 문자열이면 파싱, 비었으면 빈 블록 emptyBlockCount개 생성 */
  static validateContent(content, emptyBlockCount = 3) {
    if (typeof content === 'string') {
      if (content.trim() === '')
        return ContentUtils.createEmptyBlocks(emptyBlockCount);
      const parsed = ContentUtils.parseJSONContent(content);
      if (parsed && parsed.length > 0) return parsed;
      return ContentUtils.createEmptyBlocks(emptyBlockCount);
    }
    if (!content || content.length === 0)
      return ContentUtils.createEmptyBlocks(emptyBlockCount);
    return content;
  }

  /** 빈 블록 N개 (원본에서는 private; 호환을 위해 노출은 하되 내부 헬퍼) */
  static createEmptyBlocks(emptyBlockCount) {
    return Array.from({ length: emptyBlockCount }, () =>
      ContentUtils.createDefaultBlock(),
    );
  }
}

/** 에디터 설정 기본값/검증 유틸 */
export class EditorConfig {
  /** 표 설정 기본값 병합 (모두 기본 true) */
  static getDefaultTableConfig(userTables) {
    const u = userTables || {};
    return {
      splitCells: u.splitCells ?? true,
      cellBackgroundColor: u.cellBackgroundColor ?? true,
      cellTextColor: u.cellTextColor ?? true,
      headers: u.headers ?? true,
    };
  }

  /** 헤딩 설정 기본값 (levels 비었으면 [1..6]); 유효 시 사용자 객체 그대로 반환 */
  static getDefaultHeadingConfig(userHeading) {
    return userHeading &&
      Array.isArray(userHeading.levels) &&
      userHeading.levels.length > 0
      ? userHeading
      : { levels: [1, 2, 3, 4, 5, 6] };
  }

  /** 비활성 확장 목록 (위치 인자: userExtensions, allowVideo, allowAudio, allowFile) */
  static getDisabledExtensions(
    userExtensions,
    allowVideo = false,
    allowAudio = false,
    allowFile = false,
  ) {
    const set = new Set(userExtensions || []);
    if (!allowVideo) set.add('video');
    if (!allowAudio) set.add('audio');
    if (!allowFile) set.add('file');
    return Array.from(set);
  }
}
